import { useState, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { stylesheet } from 'typestyle';
import classnames from 'classnames';

import useMedia from '../../hooks/useMedia';

import { SeoContentStateContext } from '../../contexts/seoContent';

import type { ObjectType } from '../../types/misc';

type TProps = {
  headerOnly: boolean,
  descriptionOnly: boolean,
  headerStyles?: ObjectType,
  descriptionStyles?: ObjectType
};

type THeaderProps = {
  styles: ObjectType
};

type TDescriptionProps = {
  styles: ObjectType
};

const css = stylesheet({
  header: {
    marginTop: 18,
    marginRight: 32,
    marginLeft: 32,

    '@media (min-width: 866px) and (max-width: 890px)': {
      marginLeft: 25,
      marginRight: 25
    },

    '@media (max-width: 865px)': {
      marginLeft: 20,
      marginRight: 20
    }
  },
  marketingText: {
    marginLeft: 32,
    marginRight: 32,
    position: 'relative',

    '@media (min-width: 866px) and (max-width: 890px)': {
      marginLeft: 25,
      marginRight: 25
    },

    '@media (max-width: 865px)': {
      marginLeft: 20,
      marginRight: 20
    }
  },
  collapsedMarketingText: {
    overflow: 'hidden',
    maxHeight: 44,
    float: 'left'
  },
  collapseMarketingButton: {
    float: 'right',
  },
  collapsedMarketingButton: {
    paddingLeft: 10,
    position: 'absolute',
    right: 0,
    bottom: 0,
    background: 'white'
  }
});

function Header({ styles }: THeaderProps) {
  const { h1 } = useContext(SeoContentStateContext);

  if (h1) {
    return (
      <h1 style={styles} className={css.header}>
        {h1}
      </h1>
    );
  } else {
    return null;
  }
}

function SeoDescription({ styles }: TDescriptionProps) {
  const isMobile = useMedia(['(min-width: 768px)'], [false], true);
  const storeCollapsed = useSelector(
    state => state.productCatalogs.collapseStatus
  );
  const dispatch = useDispatch();
  const [collapsed, setCollapsed] = useState(isMobile || storeCollapsed);
  const { marketing_text } = useContext(SeoContentStateContext);

  if (!marketing_text) return null;

  return (
    <div style={styles} className={css.marketingText}>
      <p className='clearfix'>
        <span
          dangerouslySetInnerHTML={{ __html: marketing_text }}
          className={classnames({ [css.collapsedMarketingText]: collapsed })}
        />
        <ExpandableSeoDescription
          collapsed={collapsed}
          toggle={() => {
            setCollapsed(!collapsed);
            dispatch({
              type: 'UPDATE_MARKETING_TEXT_COLLAPSE_STATUS',
              status: !collapsed
            });
          }}
        />
      </p>
    </div>
  );
}

function ExpandableSeoDescription({ collapsed, toggle }) {
  return (
    <span className={
      classnames({
        [css.collapsedMarketingButton]: collapsed,
        [css.collapseMarketingButton]: true
      })

    }>
      {collapsed ? <span> &hellip; </span> : null}
      &lt;{' '}
      <button onClick={toggle} className='text-link text-underline'>
        show {collapsed ? 'more' : 'less'}
      </button>{' '}
      &gt;
    </span>
  );
}

function MarketingContent({
  headerOnly = false,
  descriptionOnly = false,
  headerStyles = {},
  descriptionStyles = {}
}: TProps) {
  if (headerOnly) {
    return <Header styles={headerStyles} />;
  } else if (descriptionOnly) {
    return (
      <SeoDescription styles={descriptionStyles} />
    );
  } else {
    return (
      <div>
        <Header styles={headerStyles} />
        <SeoDescription styles={descriptionStyles} />
      </div>
    );
  }
}

export default MarketingContent;
